import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Bookmark, CreditCard, List, LogOut, MessageSquare, Settings, Store, Zap } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import TenantSwitcher from '../TenantSwitcher';
import '../../styles/organic.css';

const STRIPE_PORTAL_URL = 'https://billing.stripe.com/p/login/6oUbJ18I46yz6oy5pu7g400';

// Shared shell for the redesigned (Organic) screens: the 76px icon rail
// with the consolidated destinations, and the account avatar whose
// menu is where Billing now lives.
//
// On a phone (≤767px, see organic.css) the rail is swapped for two bars:
// a top bar carrying identity and the account menu, and a bottom tab bar
// with the same destinations minus the desktop-only ones (`desktopOnly`
// — Tradeshow is a spreadsheet-and-wide-table tool with no phone
// layout). Both bars are always in the markup and hidden by CSS at
// desktop widths. A page that needs its own phone top
// bar — Brand detail's back row, Chat's thread switcher — passes one as
// `mobileTopBar`; it must carry the `o-topbar` class so it disappears on
// desktop like the default one does.

const STROKE = 2.75; // Organic's lucide weight — the default 2 looks wrong against Caprasimo

const NAV = [
  { key: 'movers', label: 'Movers', to: '/', Icon: Zap },
  { key: 'brands', label: 'Brands', to: '/brands', Icon: List },
  { key: 'following', label: 'Following', to: '/following', Icon: Bookmark },
  { key: 'tradeshow', label: 'Tradeshow', to: '/tradeshow', Icon: Store, desktopOnly: true },
  { key: 'chat', label: 'Chat', to: '/chat', Icon: MessageSquare },
  { key: 'settings', label: 'Settings', to: '/settings', Icon: Settings },
];

function AccountMenu({ onClose }) {
  const { user, logout } = useAuth();
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [onClose]);

  return (
    <div className="o-account-menu elev-lg" ref={ref} role="menu">
      <div className="o-account-menu-head">
        <div className="o-account-menu-name">{user?.name || 'Account'}</div>
        {user?.email && <div className="o-account-menu-org">{user.email}</div>}
      </div>
      {/* Org switching lived in the old navbar; super admins and
          multi-org users keep it here (the component self-hides
          otherwise). */}
      <TenantSwitcher />
      <a
        className="o-account-menu-item"
        role="menuitem"
        href={STRIPE_PORTAL_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClose}
      >
        <CreditCard size={14} strokeWidth={STROKE} /> Billing
      </a>
      <button type="button" className="o-account-menu-item" role="menuitem" onClick={logout}>
        <LogOut size={14} strokeWidth={STROKE} /> Logout
      </button>
    </div>
  );
}

function MobileTopBar() {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const initial = (user?.name || 'U').trim().charAt(0).toUpperCase();

  return (
    <div className="o-topbar">
      <span className="o-topbar-brand">
        <span className="o-topbar-mark">
          <Activity size={16} strokeWidth={STROKE} />
        </span>
        <span className="o-topbar-title">Lead Discovery</span>
      </span>
      <div className="o-topbar-account">
        <button
          type="button"
          className="o-topbar-avatar"
          aria-label="Account menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {initial}
        </button>
        {menuOpen && <AccountMenu onClose={() => setMenuOpen(false)} />}
      </div>
    </div>
  );
}

function MobileTabBar({ active }) {
  return (
    <nav className="o-tabbar" aria-label="Main">
      {NAV.filter((item) => !item.desktopOnly).map(({ key, label, to, Icon }) => (
        <Link
          key={key}
          to={to}
          aria-current={active === key ? 'page' : undefined}
          className={`o-tab${active === key ? ' is-active' : ''}`}
        >
          <span className="o-tab-icon">
            <Icon size={19} strokeWidth={STROKE} />
          </span>
          <span className="o-tab-label">{label}</span>
        </Link>
      ))}
    </nav>
  );
}

function OrganicShell({ active, contentStyle, aside, mobileTopBar, children }) {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const initial = (user?.name || 'U').trim().charAt(0).toUpperCase();

  return (
    <div className="organic o-shell">
      <aside className="o-rail">
        <span className="o-rail-mark" title="Lead Discovery">
          <Activity size={17} strokeWidth={STROKE} />
        </span>
        <nav className="o-rail-nav">
          {NAV.map(({ key, label, to, Icon }) => (
            <Link
              key={key}
              to={to}
              title={label}
              aria-label={label}
              aria-current={active === key ? 'page' : undefined}
              className={`o-rail-link${active === key ? ' is-active' : ''}`}
            >
              <Icon size={19} strokeWidth={STROKE} />
            </Link>
          ))}
        </nav>
        <div className="o-rail-account">
          <button
            type="button"
            className="o-rail-avatar"
            title={user?.name ? `${user.name} · Billing` : 'Account'}
            aria-label="Account menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {initial}
          </button>
          {menuOpen && <AccountMenu onClose={() => setMenuOpen(false)} />}
        </div>
      </aside>

      {mobileTopBar || <MobileTopBar />}

      <div className="o-content" style={contentStyle}>
        {children}
        <p className="o-footer">Social Listener, powered by eStreamly &mdash; 2026</p>
      </div>

      {aside}

      <MobileTabBar active={active} />
    </div>
  );
}

export default OrganicShell;
